'use client';

import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import ServiceCard, { ServiceCardData } from './ServiceCard';
import ProcessTimeline from './ProcessTimeline';
import PricingTable, { PricingPackage } from './PricingTable';
import FAQSection from './FAQSection';
import PortfolioShowcase from './PortfolioShowcase';
import CTASection from './CTASection';

interface ProcessStep {
  id: number;
  title: string;
  description: string;
  icon: string;
  duration: string;
}

interface FAQ {
  id: number;
  question: string;
  answer: string;
}

interface PortfolioItem {
  id: number;
  title: string;
  category: string;
  image: string;
  alt: string;
  serviceType: string;
}

const ServicesInteractive = () => {
  const router = useRouter();
  const [isHydrated, setIsHydrated] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>('All');

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const services: ServiceCardData[] = [
  {
    id: 1,
    title: "Brand Identity Design",
    description: "Complete brand identity packages that blend Indian cultural elements with modern design principles for authentic market presence.",
    icon: "SparklesIcon",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_13d615655-1764663842689.png",
    alt: "Modern brand identity design mockup showing logo variations on business cards and stationery with warm color palette",
    features: [
    { text: "Logo design with cultural resonance" },
    { text: "Brand guidelines & style guide" },
    { text: "Business card & stationery design" },
    { text: "Social media brand kit" }],

    startingPrice: "₹25,000",
    deliveryTime: "2-3 weeks",
    category: "Branding"
  },
  {
    id: 2,
    title: "Website Design & Development",
    description: "Responsive, SEO-optimized websites that convert visitors into customers with culturally relevant design and user experience.",
    icon: "GlobeAltIcon",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1f764f3ac-1764655547252.png",
    alt: "Professional web designer working on responsive website layout on multiple screens showing mobile and desktop views",
    features: [
    { text: "Mobile-first responsive design" },
    { text: "SEO optimization & fast loading" },
    { text: "Content management system" },
    { text: "3 months free maintenance" }],

    startingPrice: "₹45,000",
    deliveryTime: "4-6 weeks",
    category: "Web Design"
  },
  {
    id: 3,
    title: "E-Commerce Solutions",
    description: "Complete online store setup with payment integration, inventory management, and conversion-optimized design for Indian market.",
    icon: "ShoppingCartIcon",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1eb7c1cb3-1764655134961.png",
    alt: "Modern e-commerce website interface showing product catalog with shopping cart and payment options on laptop screen",
    features: [
    { text: "Payment gateway integration" },
    { text: "Product catalog management" },
    { text: "Order tracking system" },
    { text: "Mobile app-like experience" }],

    startingPrice: "₹75,000",
    deliveryTime: "6-8 weeks",
    category: "E-Commerce"
  },
  {
    id: 4,
    title: "Social Media Design",
    description: "Engaging social media graphics and content templates that maintain brand consistency across all platforms.",
    icon: "PhotoIcon",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_107b4258c-1766546375647.png",
    alt: "Creative social media post designs displayed on smartphone screen showing colorful graphics and engaging content layouts",
    features: [
    { text: "30 custom post designs" },
    { text: "Story & reel templates" },
    { text: "Profile optimization" },
    { text: "Content calendar planning" }],

    startingPrice: "₹15,000",
    deliveryTime: "1-2 weeks",
    category: "Social Media"
  },
  {
    id: 5,
    title: "Print Design Services",
    description: "Professional print materials including brochures, posters, and packaging that reflect your brand's cultural identity.",
    icon: "DocumentTextIcon",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1069e529d-1764676026100.png",
    alt: "Professional print design materials including colorful brochures, business cards and marketing collateral spread on desk",
    features: [
    { text: "Brochure & catalog design" },
    { text: "Poster & banner design" },
    { text: "Packaging design" },
    { text: "Print-ready file delivery" }],

    startingPrice: "₹12,000",
    deliveryTime: "1-2 weeks",
    category: "Print Design"
  },
  {
    id: 6,
    title: "Digital Marketing Design",
    description: "Performance-focused ad creatives and landing pages designed to maximize conversions and ROI for Indian audiences.",
    icon: "ChartBarIcon",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1997a6356-1764656421129.png",
    alt: "Digital marketing analytics dashboard showing campaign performance metrics and colorful graphs on computer monitor",
    features: [
    { text: "Ad creative design (Google, FB)" },
    { text: "Landing page design" },
    { text: "Email template design" },
    { text: "A/B testing variations" }],

    startingPrice: "₹20,000",
    deliveryTime: "2-3 weeks",
    category: "Marketing"
  }];


  const processSteps: ProcessStep[] = [
  {
    id: 1,
    title: "Discovery & Research",
    description: "We begin by understanding your business, target audience, cultural context, and competitive landscape to create a solid foundation for your project.",
    icon: "MagnifyingGlassIcon",
    duration: "3-5 days"
  },
  {
    id: 2,
    title: "Strategy & Planning",
    description: "Develop a comprehensive design strategy that aligns with your business goals while incorporating culturally relevant elements that resonate with your audience.",
    icon: "LightBulbIcon",
    duration: "2-3 days"
  },
  {
    id: 3,
    title: "Design & Creation",
    description: "Our team creates initial design concepts blending traditional Indian aesthetics with modern design principles, ensuring both beauty and functionality.",
    icon: "PaintBrushIcon",
    duration: "1-2 weeks"
  },
  {
    id: 4,
    title: "Review & Refinement",
    description: "Collaborate with you to refine designs based on feedback, ensuring every element perfectly represents your brand and meets your expectations.",
    icon: "ArrowPathIcon",
    duration: "3-5 days"
  },
  {
    id: 5,
    title: "Delivery & Support",
    description: "Final delivery of all assets with comprehensive guidelines, plus ongoing support to ensure successful implementation and brand consistency.",
    icon: "CheckBadgeIcon",
    duration: "2-3 days"
  }];


  const pricingPackages: PricingPackage[] = [
  {
    id: 1,
    name: "Starter",
    description: "Perfect for new businesses and startups",
    price: "₹25,000",
    duration: "One-time payment",
    popular: false,
    cta: "Get Started",
    features: [
    { text: "Logo design (3 concepts)", included: true },
    { text: "Basic brand guidelines", included: true },
    { text: "Business card design", included: true },
    { text: "Social media profile setup", included: true },
    { text: "Website design", included: false },
    { text: "Marketing materials", included: false },
    { text: "Priority support", included: false }]

  },
  {
    id: 2,
    name: "Professional",
    description: "Ideal for growing businesses",
    price: "₹75,000",
    duration: "One-time payment",
    popular: true,
    cta: "Most Popular",
    features: [
    { text: "Complete brand identity", included: true },
    { text: "Comprehensive brand guidelines", included: true },
    { text: "Full stationery suite", included: true },
    { text: "5-page website design", included: true },
    { text: "Social media templates (20)", included: true },
    { text: "3 months support", included: true },
    { text: "Priority support", included: true }]

  },
  {
    id: 3,
    name: "Enterprise",
    description: "Complete solution for established brands",
    price: "₹1,50,000",
    duration: "One-time payment",
    popular: false,
    cta: "Contact Us",
    features: [
    { text: "Full brand identity system", included: true },
    { text: "Advanced brand guidelines", included: true },
    { text: "Complete website (10+ pages)", included: true },
    { text: "E-commerce integration", included: true },
    { text: "Marketing campaign design", included: true },
    { text: "6 months support", included: true },
    { text: "Dedicated account manager", included: true }]

  }];


  const faqs: FAQ[] = [
  {
    id: 1,
    question: "What makes DesiSocio different from other design agencies?",
    answer: "We specialize in blending authentic Indian cultural elements with contemporary design principles. Our deep understanding of Indian business practices, regional nuances, and cultural sensibilities allows us to create designs that truly resonate with your target audience while maintaining global appeal."
  },
  {
    id: 2,
    question: "How long does a typical project take?",
    answer: "Project timelines vary based on scope and complexity. A basic logo design takes 1-2 weeks, complete brand identity 2-3 weeks, and full website development 4-6 weeks. We provide detailed timelines during the initial consultation and keep you updated throughout the process."
  },
  {
    id: 3,
    question: "Do you offer revisions?",
    answer: "Yes! All our packages include multiple revision rounds. Starter package includes 2 revision rounds, Professional includes 3 rounds, and Enterprise includes unlimited revisions. We work closely with you until you're completely satisfied with the final design."
  },
  {
    id: 4,
    question: "What file formats will I receive?",
    answer: "You'll receive all source files and multiple export formats including AI, PSD, PDF, PNG, JPG, and SVG. For websites, you get complete source code, documentation, and hosting setup assistance. We ensure you have everything needed for both digital and print use."
  },
  {
    id: 5,
    question: "Do you provide ongoing support after project completion?",
    answer: "Absolutely! All packages include post-delivery support. Starter gets 1 month, Professional gets 3 months, and Enterprise gets 6 months of free support. This includes minor updates, technical assistance, and guidance on implementing your new brand assets."
  },
  {
    id: 6,
    question: "Can you work with businesses outside India?",
    answer: "Yes! While we specialize in Indian market understanding, we work with clients globally. Our cultural design expertise is particularly valuable for businesses targeting Indian diaspora communities or entering the Indian market."
  },
  {
    id: 7,
    question: "What payment methods do you accept?",
    answer: "We accept bank transfers (NEFT/RTGS/IMPS), UPI payments, and online payment gateways. For larger projects, we offer milestone-based payment plans: 50% upfront, 30% at mid-point, and 20% on final delivery."
  },
  {
    id: 8,
    question: "Do you sign NDAs for confidential projects?",
    answer: "Yes, we're happy to sign Non-Disclosure Agreements before starting any project. Your business information, ideas, and project details are always kept strictly confidential."
  }];


  const portfolioItems: PortfolioItem[] = [
  {
    id: 1,
    title: "Spice Heritage Brand",
    category: "Food & Beverage",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1077cc7e6-1764635514674.png",
    alt: "Traditional Indian spice brand packaging design featuring warm saffron colors and cultural patterns on product boxes",
    serviceType: "Brand Identity"
  },
  {
    id: 2,
    title: "Textile E-Commerce",
    category: "Fashion & Retail",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1b71e654a-1767339934944.png",
    alt: "Modern e-commerce website for traditional Indian textiles showing colorful sarees and fabrics on laptop screen",
    serviceType: "Web Design"
  },
  {
    id: 3,
    title: "Wellness Startup",
    category: "Health & Wellness",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_123eb8c16-1768016879586.png",
    alt: "Peaceful wellness brand identity featuring yoga poses and natural elements in calming teal and green colors",
    serviceType: "Brand Identity"
  },
  {
    id: 4,
    title: "Restaurant Campaign",
    category: "Food & Hospitality",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_12b4a0b2d-1767380870132.png",
    alt: "Vibrant restaurant social media campaign showing delicious Indian cuisine dishes with engaging graphics and text",
    serviceType: "Social Media"
  },
  {
    id: 5,
    title: "Jewelry Boutique",
    category: "Luxury & Fashion",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1f720b74b-1767079672809.png",
    alt: "Elegant jewelry boutique website design showcasing traditional Indian gold ornaments with premium aesthetic",
    serviceType: "Web Design"
  },
  {
    id: 6,
    title: "Education Platform",
    category: "EdTech",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_146254e45-1764672099813.png",
    alt: "Modern educational platform interface showing online learning dashboard with colorful course cards and progress tracking",
    serviceType: "Web Design"
  }];


  const categories = ['All', 'Branding', 'Web Design', 'E-Commerce', 'Social Media', 'Print Design', 'Marketing'];

  const filteredServices = selectedCategory === 'All' ?
  services :
  services.filter((service) => service.category === selectedCategory);

  const handleLearnMore = (serviceId: number) => {
    if (!isHydrated) return;
    const service = services.find((s) => s.id === serviceId);
    if (service) {
      router.push(`/services#service-${serviceId}`);
    }
  };

  const handleGetQuote = (serviceId: number) => {
    if (!isHydrated) return;
    router.push(`/quote-request?service=${serviceId}`);
  };

  const handleSelectPackage = (packageId: number) => {
    if (!isHydrated) return;
    router.push(`/quote-request?package=${packageId}`);
  };

  const handleViewProject = (projectId: number) => {
    if (!isHydrated) return;
    router.push(`/portfolio#project-${projectId}`);
  };

  const handleGetStarted = () => {
    if (!isHydrated) return;
    router.push('/quote-request');
  };

  const handleScheduleCall = () => {
    if (!isHydrated) return;
    router.push('/contact');
  };

  if (!isHydrated) {
    return (
      <div data-component-id="src/app/services/components/ServicesInteractive.tsx:379:6" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="379" data-component-end-line="391" data-component-file="ServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20bg-background%22%7D" className="min-h-screen bg-background">
        <div data-component-id="src/app/services/components/ServicesInteractive.tsx:380:8" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="380" data-component-end-line="390" data-component-file="ServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22container%20mx-auto%20px-4%20py-16%22%7D" className="container mx-auto px-4 py-16">
          <div data-component-id="src/app/services/components/ServicesInteractive.tsx:381:10" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="381" data-component-end-line="389" data-component-file="ServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22animate-pulse%20space-y-8%22%7D" className="animate-pulse space-y-8">
            <div data-component-id="src/app/services/components/ServicesInteractive.tsx:382:12" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="382" data-component-end-line="382" data-component-file="ServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-12%20bg-muted%20rounded%20w-3%2F4%20mx-auto%22%7D" className="h-12 bg-muted rounded w-3/4 mx-auto" />
            <div data-component-id="src/app/services/components/ServicesInteractive.tsx:383:12" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="383" data-component-end-line="383" data-component-file="ServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-6%20bg-muted%20rounded%20w-1%2F2%20mx-auto%22%7D" className="h-6 bg-muted rounded w-1/2 mx-auto" />
            <div data-component-id="src/app/services/components/ServicesInteractive.tsx:384:12" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="384" data-component-end-line="388" data-component-file="ServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20md%3Agrid-cols-2%20lg%3Agrid-cols-3%20gap-8%22%7D" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3, 4, 5, 6].map((i) =>
              <div data-component-id="src/app/services/components/ServicesInteractive.tsx:386:14" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="386" data-component-end-line="386" data-component-file="ServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-96%20bg-muted%20rounded-xl%22%7D" key={i} className="h-96 bg-muted rounded-xl" />
              )}
            </div>
          </div>
        </div>
      </div>);

  }

  return (
    <div data-component-id="src/app/services/components/ServicesInteractive.tsx:396:4" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="396" data-component-end-line="433" data-component-file="ServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20bg-background%22%7D" className="min-h-screen bg-background">
      <section data-component-id="src/app/services/components/ServicesInteractive.tsx:397:6" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="397" data-component-end-line="426" data-component-file="ServicesInteractive.tsx" data-component-name="section" data-component-content="%7B%22elementName%22%3A%22section%22%2C%22className%22%3A%22py-12%20lg%3Apy-16%20bg-background%22%7D" className="py-12 lg:py-16 bg-background">
        <div data-component-id="src/app/services/components/ServicesInteractive.tsx:398:8" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="398" data-component-end-line="425" data-component-file="ServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22container%20mx-auto%20px-4%22%7D" className="container mx-auto px-4">
          <div data-component-id="src/app/services/components/ServicesInteractive.tsx:399:10" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="399" data-component-end-line="413" data-component-file="ServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-wrap%20justify-center%20gap-3%20mb-12%22%7D" className="flex flex-wrap justify-center gap-3 mb-12">
            {categories.map((category) =>
            <button data-component-id="src/app/services/components/ServicesInteractive.tsx:401:12" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="401" data-component-end-line="411" data-component-file="ServicesInteractive.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%7D"
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`px-6 py-2.5 rounded-full font-cta font-semibold text-sm transition-all duration-300 ${
              selectedCategory === category ?
              'bg-primary text-primary-foreground shadow-card' :
              'bg-muted text-muted-foreground hover:bg-primary/10 hover:text-primary'}`
              }>

                {category}
              </button>
            )}
          </div>

          <div data-component-id="src/app/services/components/ServicesInteractive.tsx:415:10" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="415" data-component-end-line="424" data-component-file="ServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20md%3Agrid-cols-2%20lg%3Agrid-cols-3%20gap-8%20max-w-7xl%20mx-auto%22%7D" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {filteredServices.map((service) =>
            <ServiceCard data-component-id="src/app/services/components/ServicesInteractive.tsx:417:12" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="417" data-component-end-line="421" data-component-file="ServicesInteractive.tsx" data-component-name="ServiceCard" data-component-content="%7B%22elementName%22%3A%22ServiceCard%22%7D"
              key={service.id}
              service={service}
              onLearnMore={handleLearnMore}
              onGetQuote={handleGetQuote} />

            )}
          </div>
        </div>
      </section>

      <ProcessTimeline data-component-id="src/app/services/components/ServicesInteractive.tsx:428:6" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="428" data-component-end-line="428" data-component-file="ServicesInteractive.tsx" data-component-name="ProcessTimeline" data-component-content="%7B%22elementName%22%3A%22ProcessTimeline%22%7D" steps={processSteps} />
      <PricingTable data-component-id="src/app/services/components/ServicesInteractive.tsx:429:6" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="429" data-component-end-line="429" data-component-file="ServicesInteractive.tsx" data-component-name="PricingTable" data-component-content="%7B%22elementName%22%3A%22PricingTable%22%7D" packages={pricingPackages} onSelectPackage={handleSelectPackage} />
      <PortfolioShowcase data-component-id="src/app/services/components/ServicesInteractive.tsx:430:6" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="430" data-component-end-line="430" data-component-file="ServicesInteractive.tsx" data-component-name="PortfolioShowcase" data-component-content="%7B%22elementName%22%3A%22PortfolioShowcase%22%7D" items={portfolioItems} onViewProject={handleViewProject} />
      <FAQSection data-component-id="src/app/services/components/ServicesInteractive.tsx:431:6" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="431" data-component-end-line="431" data-component-file="ServicesInteractive.tsx" data-component-name="FAQSection" data-component-content="%7B%22elementName%22%3A%22FAQSection%22%7D" faqs={faqs} />
      <CTASection data-component-id="src/app/services/components/ServicesInteractive.tsx:432:6" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="432" data-component-end-line="432" data-component-file="ServicesInteractive.tsx" data-component-name="CTASection" data-component-content="%7B%22elementName%22%3A%22CTASection%22%7D" onGetStarted={handleGetStarted} onScheduleCall={handleScheduleCall} />
    </div>);

};

export default ServicesInteractive;